import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';

export interface TeamOverview {
  summary: {
    total_promoters: number;
    in_field: number;
    delayed: number;
    absent: number;
    finished: number;
    total_stores_today: number;
    stores_visited: number;
    completion_rate: number;
  };
  promoters: PromoterOverview[];
  alerts: TeamAlert[];
}

export interface PromoterOverview {
  promoter_id: string;
  name: string;
  phone: string;
  email: string;
  status: 'in_field' | 'delayed' | 'absent' | 'finished' | 'in_transit';
  current_store: {
    store_id: string;
    name: string;
    check_in_at: string;
    duration_minutes: number;
    photo_count: number;
  } | null;
  route_progress: {
    visited: number;
    total: number;
    stores: { store_id: string; name: string; status: string; score?: number }[];
  };
  last_location: {
    latitude: number;
    longitude: number;
    updated_at: string;
  } | null;
  today_stats: {
    avg_score: number | null;
    total_photos: number;
    first_checkin: string | null;
  };
}

export interface TeamAlert {
  id: string;
  type: string;
  severity: string;
  promoter_id: string;
  promoter_name: string;
  message: string;
  created_at: string;
  status: string;
}

const CACHE_KEY = 'supervisor-team-overview-cache';

export function useTeamOverview(refetchInterval = 60000) {
  const result = useQuery<TeamOverview>({
    queryKey: ['supervisor', 'team-overview'],
    queryFn: async () => {
      const data = await api.get('/supervisor/team-overview').then(res => res.data.data);
      // Save to localStorage for offline fallback
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({ data, timestamp: Date.now() }));
      } catch {}
      return data;
    },
    refetchInterval,
    staleTime: 30000,
    // Offline: use cached data as initialData
    initialData: () => {
      try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) return JSON.parse(cached).data;
      } catch {}
      return undefined;
    },
  });

  // Timestamp of last successful fetch (for offline banner)
  // Wrapped in useMemo so it is not re-computed on every render,
  // and recalculates when the query data refreshes.
  const cachedTimestamp = useMemo(() => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) return JSON.parse(cached).timestamp as number;
    } catch {}
    return null;
  }, [result.dataUpdatedAt]);

  return { ...result, cachedTimestamp };
}
